import axios from 'axios';
import {API_URL} from '../config/constants';
import {
    ComponentMappingStatusSummary,
    ComponentReportMappingState,
    StartMappingRunOptions,
} from '../@types/component-mapping-state';
import {getErrorMessage} from '../utils/utils';

/**
 * Client for the Component mapping run endpoints (backend `ComponentReportMappingStateController`,
 * base `/api/v2/components`). A run is scoped to one (component version, report source) pair;
 * the source is addressed by its name (e.g. NTSB).
 */
class ComponentReportMappingStateService {
    static baseUrl = API_URL + 'api/v2/components';

    private static sourceUrl(componentId: number | string, sourceName: string): string {
        return `${ComponentReportMappingStateService.baseUrl}/${componentId}/sources/${encodeURIComponent(sourceName)}`;
    }

    /** Submits an mapping run of a Component against a report source. Returns the QUEUED state. */
    startMappingRun(
        componentId: number | string,
        sourceName: string,
        options: StartMappingRunOptions = {},
    ): Promise<ComponentReportMappingState> {
        const params = new URLSearchParams();
        if (options.mode) {
            params.set('mode', options.mode);
        }
        if (options.limit != null) {
            params.set('limit', String(options.limit));
        }
        const query = params.toString() ? `?${params.toString()}` : '';
        return axios
            .post(
                `${ComponentReportMappingStateService.sourceUrl(componentId, sourceName)}/mapping-run${query}`,
                {},
                {withCredentials: true, headers: {'Content-Type': 'application/json'}},
            )
            .then(response => response.data as ComponentReportMappingState)
            .catch(err => {
                throw new Error('Failed to start the mapping run: ' + getErrorMessage(err));
            });
    }

    /** Cancels the in-flight run for a (component, source) pair. */
    cancelMappingRun(componentId: number | string, sourceName: string): Promise<void> {
        return axios
            .post(
                `${ComponentReportMappingStateService.sourceUrl(componentId, sourceName)}/mapping-run/cancel`,
                {},
                {withCredentials: true, headers: {'Content-Type': 'application/json'}},
            )
            .then(() => undefined)
            .catch(err => {
                throw new Error('Failed to cancel the mapping run: ' + getErrorMessage(err));
            });
    }

    /**
     * Fetches the run state for a (component, source) pair, or {@code null} when the Component
     * has never been run against that source (backend returns 404).
     */
    getMappingRunState(componentId: number | string, sourceName: string): Promise<ComponentReportMappingState | null> {
        return axios
            .get(`${ComponentReportMappingStateService.sourceUrl(componentId, sourceName)}/mapping-run`, {withCredentials: true})
            .then(response => response.data as ComponentReportMappingState)
            .catch(err => {
                if (err?.response?.status === 404) {
                    return null;
                }
                throw new Error('Failed to get the mapping run state: ' + getErrorMessage(err));
            });
    }

    /**
     * Where a Component version stands against one source: the derived status and the counts
     * behind it, computed from the mapping records at the moment it is asked for.
     */
    getMappingStatus(componentId: number | string, sourceName: string): Promise<ComponentMappingStatusSummary> {
        return axios
            .get(
                `${ComponentReportMappingStateService.baseUrl}/${componentId}` +
                    `/sources/${encodeURIComponent(sourceName)}/mapping-status`,
                {withCredentials: true},
            )
            .then(response => response.data as ComponentMappingStatusSummary)
            .catch(err => {
                throw new Error('Failed to get mapping status: ' + getErrorMessage(err));
            });
    }

    /**
     * The mapping status against every source this version has been run against, in one call. The
     * source table leads with this rather than with the last run's state, because a completed run
     * of a hundred reports leaves a source of fifty thousand almost entirely unmapped.
     */
    listMappingStatuses(componentId: number | string): Promise<ComponentMappingStatusSummary[]> {
        return axios
            .get(`${ComponentReportMappingStateService.baseUrl}/${componentId}/sources/mapping-status`, {withCredentials: true})
            .then(response => {
                // A backend without this endpoint forwards the unknown path to the web app's index
                // page, which arrives as a successful HTML response rather than an error. Read as
                // an empty list, that showed every source as not mapped.
                if (!Array.isArray(response.data)) {
                    throw new Error('The server did not return mapping statuses. It may need restarting.');
                }
                return response.data as ComponentMappingStatusSummary[];
            })
            .catch(err => {
                throw new Error('Failed to list mapping statuses: ' + getErrorMessage(err));
            });
    }

    /** Lists every report source a Component version has been run against, with run status. */
    listSourceStates(componentId: number | string): Promise<ComponentReportMappingState[]> {
        return axios
            .get(`${ComponentReportMappingStateService.baseUrl}/${componentId}/sources`, {withCredentials: true})
            .then(response => (Array.isArray(response.data) ? (response.data as ComponentReportMappingState[]) : []))
            .catch(err => {
                throw new Error('Failed to list source states: ' + getErrorMessage(err));
            });
    }
}

export default new ComponentReportMappingStateService();
